import { Button, Label, Select, Spinner, Text, makeStyles, tokens, useId } from '@fluentui/react-components';
import {
  ArrowTrendingLines24Regular,
  Building24Regular,
  Map20Regular,
  PeopleTeam24Regular,
  Trophy24Regular,
} from '@fluentui/react-icons';
import { findRecordPage, QueryAggregate, readColumnRawValue, recordPageUrl, type QueryRequest, type TableRecord } from '@powerportalspro/core';
import { usePowerPortalsPro, useRecordPages, useT } from '@powerportalspro/react';
import { RecordLink, SummaryCard, SummaryCards } from '@powerportalspro/react-fluent';
import { Map, type MapItem, type MapStyle } from '@powerportalspro/react-maps';
import { useEffect, useMemo, useState } from 'react';
import { companiesIn, MIDWEST, NORTHEAST, SOUTHEAST, SOUTHWEST, statesCondition, WEST, type DemoRegion } from './DemoRegion';

// Reuses the Blazor demo's localization keys (MapShowcaseMainDemo) so both stacks share strings.
const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.MapShowcase.MapShowcaseMainDemo';

const STYLES: MapStyle[] = ['road', 'satellite', 'hybrid'];

// The views the server registers for this map: accounts and contacts, which it opens on, or either alone.
const ACCOUNTS_AND_CONTACTS_VIEW_ID = '4e7b1c92-5a3d-4f08-b6e1-9c2d7a5f3e10';
const ACCOUNTS_VIEW_ID = '4e7b1c92-5a3d-4f08-b6e1-9c2d7a5f3e11';
const CONTACTS_VIEW_ID = '4e7b1c92-5a3d-4f08-b6e1-9c2d7a5f3e12';
const MAP_VIEW_IDS = [ACCOUNTS_AND_CONTACTS_VIEW_ID, ACCOUNTS_VIEW_ID, CONTACTS_VIEW_ID];

// Where the map opens: on the lower 48, rather than fitted to every region — Alaska and Hawaii with
// them would zoom it out to half the world.
const LOWER_FORTY_EIGHT = { latitude: 39.0, longitude: -96.5 };

// The address search finds places in the US, where the regions are.
const UNITED_STATES = ['US'];

// A sales region — its states, which the figures are filtered by, the regions the Kanban board demos
// offer views of — with its color and its outline on the map: one shape for the lower 48's part of it,
// and one each for Alaska and Hawaii.
interface Region extends DemoRegion {
  color: string;
  shapes: [number, number][][];
}

const REGIONS: Region[] = [
  {
    ...WEST,
    color: 'var(--colorPaletteBlueBorderActive)',
    shapes: [
      [
        [48.4, -124.75], [49.0, -123.3], [49.0, -104.05], [45.0, -104.05], [41.0, -104.05], [41.0, -102.05],
        [37.0, -102.05], [37.0, -109.05], [37.0, -114.05], [36.1, -114.05], [35.0, -114.63], [32.72, -114.72],
        [32.53, -117.12], [33.9, -118.5], [34.45, -120.47], [36.6, -121.95], [37.8, -122.55], [40.4, -124.4],
        [42.0, -124.4], [46.2, -124.05], [47.9, -124.75],
      ],
      [
        [69.65, -141.0], [70.1, -143.5], [70.5, -150.0], [71.4, -156.6], [70.3, -161.9], [68.9, -166.2],
        [67.1, -163.8], [65.6, -168.1], [64.4, -166.2], [63.6, -161.0], [61.5, -166.2], [58.7, -157.0],
        [55.0, -163.5], [55.5, -161.0], [57.5, -154.0], [59.3, -151.7], [60.4, -147.0], [59.9, -141.0],
        [59.5, -139.5], [58.2, -136.6], [56.0, -133.0], [54.7, -130.6], [56.1, -130.0], [57.0, -132.0],
        [59.0, -135.3], [60.3, -139.0], [60.3, -141.0],
      ],
      [
        [22.4, -160.4], [22.4, -154.6], [18.8, -154.6], [18.8, -160.4],
      ],
    ],
  },
  {
    ...SOUTHWEST,
    color: 'var(--colorPaletteDarkOrangeBorderActive)',
    shapes: [
      [
        [37.0, -114.05], [37.0, -109.05], [37.0, -102.05], [37.0, -94.62], [35.4, -94.43], [33.64, -94.48],
        [33.6, -94.04], [31.0, -94.04], [29.7, -93.84], [28.9, -95.3], [27.8, -97.1], [26.0, -97.2],
        [26.1, -98.3], [27.5, -99.5], [29.4, -101.0], [29.8, -102.4], [29.0, -103.2], [29.8, -104.6],
        [31.78, -106.53], [31.78, -108.21], [31.33, -108.21], [31.33, -111.07], [32.49, -114.81], [32.72, -114.72],
        [35.0, -114.63], [36.1, -114.05],
      ],
    ],
  },
  {
    ...MIDWEST,
    color: 'var(--colorPaletteGreenBorderActive)',
    shapes: [
      [
        [49.0, -104.05], [49.0, -95.15], [49.38, -95.15], [48.0, -89.6], [47.7, -85.0], [46.5, -84.4],
        [45.3, -82.5], [43.0, -82.4], [42.3, -83.1], [41.7, -83.0], [42.3, -80.5], [41.98, -80.52],
        [40.64, -80.52], [40.4, -80.62], [40.0, -80.76], [39.4, -81.4], [38.6, -82.6], [38.7, -83.7],
        [39.1, -84.8], [38.0, -86.5], [37.8, -88.0], [37.0, -89.1], [36.5, -89.5], [36.0, -89.7],
        [36.0, -90.37], [36.5, -90.15], [36.5, -94.62], [37.0, -94.62], [37.0, -102.05], [41.0, -102.05],
        [41.0, -104.05], [45.0, -104.05],
      ],
    ],
  },
  {
    ...SOUTHEAST,
    color: 'var(--colorPaletteRedBorderActive)',
    shapes: [
      [
        [36.5, -94.62], [35.4, -94.43], [33.64, -94.48], [33.6, -94.04], [31.0, -94.04], [29.7, -93.84],
        [29.5, -92.3], [29.0, -90.0], [30.2, -89.4], [30.4, -88.0], [30.4, -86.5], [29.7, -85.0],
        [30.0, -84.0], [29.0, -82.8], [27.8, -82.8], [26.0, -81.7], [25.1, -80.9], [25.3, -80.3],
        [26.7, -80.0], [28.4, -80.6], [30.4, -81.4], [32.0, -80.8], [32.8, -79.9], [33.8, -78.5],
        [34.6, -76.5], [35.2, -75.5], [36.55, -75.87], [38.0, -75.25], [38.0, -76.25], [38.3, -77.0],
        [38.8, -77.03], [38.95, -77.12], [39.3, -77.7], [39.6, -78.3], [39.2, -79.48], [39.72, -79.48],
        [39.72, -80.52], [40.64, -80.52], [40.4, -80.62], [40.0, -80.76], [39.4, -81.4], [38.6, -82.6],
        [38.7, -83.7], [39.1, -84.8], [38.0, -86.5], [37.8, -88.0], [37.0, -89.1], [36.5, -89.5],
        [36.0, -89.7], [36.0, -90.37], [36.5, -90.15],
      ],
    ],
  },
  {
    ...NORTHEAST,
    color: 'var(--colorPalettePurpleBorderActive)',
    shapes: [
      [
        [41.98, -80.52], [42.27, -79.76], [42.5, -79.3], [43.1, -79.05], [43.6, -77.0], [44.1, -76.4],
        [45.0, -74.7], [45.0, -71.5], [45.3, -71.08], [46.4, -70.0], [47.4, -69.2], [47.3, -68.2],
        [47.1, -67.8], [45.9, -67.8], [45.2, -67.3], [44.8, -66.95], [44.3, -68.3], [43.6, -70.2],
        [42.6, -70.6], [42.3, -70.95], [41.7, -69.9], [41.3, -70.0], [41.3, -71.9], [41.1, -71.85],
        [40.6, -73.9], [40.4, -74.0], [38.9, -74.9], [38.45, -75.05], [38.0, -75.25], [38.0, -76.25],
        [38.3, -77.0], [38.8, -77.03], [38.95, -77.12], [39.3, -77.7], [39.6, -78.3], [39.2, -79.48],
        [39.72, -79.48], [39.72, -80.52], [40.64, -80.52],
      ],
    ],
  },
];

// The table's active rows in a region's states — those whose address is in one — or all of them.
function inRegion(table: string, region: Region | null): string {
  return `
<fetch>
  <entity name="${table}">
    <filter>
      <condition attribute="statecode" operator="eq" value="0" />
      ${region ? statesCondition(region) : ''}
    </filter>
  </entity>
</fetch>`;
}

// Opportunities of one status — 0 open, 1 won — whose account is in a region, or all of them.
function opportunitiesInRegion(status: number, region: Region | null): string {
  const account = region ? companiesIn(region, 'customerid') : '';
  return `
<fetch>
  <entity name="opportunity">
    <filter>
      <condition attribute="statecode" operator="eq" value="${status}" />
    </filter>
    ${account}
  </entity>
</fetch>`;
}

// A region's figures, or the whole country's, formatted for the visitor by the server.
interface Figures {
  accounts?: string | null | undefined;
  contacts?: string | null | undefined;
  won?: string | null | undefined;
  wonDeals?: string | null | undefined;
  pipeline?: string | null | undefined;
  openDeals?: string | null | undefined;
}

type ColumnAggregate = NonNullable<QueryRequest['columnAggregates']>[number];

// A query that answers only with its figures, worked out by Dataverse.
function figuresOf(fetchXml: string, ...figures: ColumnAggregate[]): QueryRequest {
  return {
    fetchXml,
    columns: [...new Set(figures.map((figure) => figure.columnName))],
    columnAggregates: figures,
    figuresOnly: true,
  };
}

const count = (columnName: string): ColumnAggregate => ({ columnName, aggregate: QueryAggregate.Count });
const sum = (columnName: string): ColumnAggregate => ({ columnName, aggregate: QueryAggregate.Sum, format: 'C0' });

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXL,
    marginBottom: tokens.spacingVerticalL,
  },
  labeled: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  select: { width: '160px' },
  figuresHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    margin: `${tokens.spacingVerticalL} 0 ${tokens.spacingVerticalXS}`,
  },
  // A region's popup: its name beside an icon in its color, then its figures two by two, each a
  // label over its value.
  region: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '260px' },
  heading: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  icon: {
    display: 'inline-flex',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  figures: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
    margin: 0,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  value: { margin: 0 },
  muted: { color: tokens.colorNeutralForeground3 },
});

// A region's popup: its figures once they have come, a spinner until then.
function RegionCard({ region, figures }: { region: Region; figures: Figures | null }) {
  const t = useT();
  const styles = useStyles();
  const figure = (key: string, value: string | null | undefined, caption?: string) => (
    <div key={key}>
      <dt>
        <Text size={200} className={styles.muted}>
          {t(`${STRINGS_BASE}.${key}`)}
        </Text>
      </dt>
      <dd className={styles.value}>
        <Text size={400} weight="semibold" block>
          {value ?? '—'}
        </Text>
        {caption && (
          <Text size={200} block className={styles.muted}>
            {caption}
          </Text>
        )}
      </dd>
    </div>
  );

  return (
    <div className={styles.region}>
      <div className={styles.heading}>
        <span className={styles.icon}>
          <Map20Regular style={{ color: region.color }} />
        </span>
        <div>
          <Text size={400} weight="semibold" block>
            {t(`${STRINGS_BASE}.region-${region.key}`)}
          </Text>
          <Text size={200} block className={styles.muted}>
            {t(`${STRINGS_BASE}.states-count`, [region.states.length])}
          </Text>
        </div>
      </div>
      {figures ? (
        <dl className={styles.figures}>
          {figure('accounts', figures.accounts)}
          {figure('contacts', figures.contacts)}
          {figure('won', figures.won, figures.wonDeals ? t(`${STRINGS_BASE}.won-deals`, [figures.wonDeals]) : undefined)}
          {figure('pipeline', figures.pipeline, figures.openDeals ? t(`${STRINGS_BASE}.open-deals`, [figures.openDeals]) : undefined)}
        </dl>
      ) : (
        <Spinner size="small" />
      )}
    </div>
  );
}

// Where a row is: its city and state.
function place(record: TableRecord): string {
  return ['address1_city', 'address1_stateorprovince']
    .map((column) => readColumnRawValue<string>(record, column))
    .filter(Boolean)
    .join(', ');
}

/**
 * The US in five sales regions, over the accounts and contacts the server registers for maps
 * (ForMaps), each table in its own color, on the portal's own map provider. The map's toolbar
 * chooses the view and the tables shown, filters them, and keeps a signed-in visitor's views.
 * Clicking a region shows its figures in its popup and under the map: its accounts and contacts,
 * and its accounts' won and open opportunities, which Dataverse works out over FetchXML queries of
 * the region's states, read with the visitor's permissions. A row's pin shows what it is and where,
 * its name linked to its record's page, registered with AddRecordPage.
 */
export function MapShowcaseMainSample() {
  const t = useT();
  const styles = useStyles();
  const styleId = useId('map-style');
  const { ppp } = usePowerPortalsPro();
  // The record pages the portal registers with AddRecordPage, which a row's name links to.
  const recordPages = useRecordPages();
  const [style, setStyle] = useState<MapStyle>('road');
  const [region, setRegion] = useState<Region | null>(null);
  // What the figures under the map are of, and the printout's title: the region shown, or every region.
  const regionTitle = region ? t(`${STRINGS_BASE}.region-${region.key}`) : t(`${STRINGS_BASE}.all-regions`);
  // The figures and the region they are of: a spinner in its popup until they come.
  const [figures, setFigures] = useState<{ of: Region | null; figures: Figures } | null>(null);

  const regionItems = useMemo<MapItem<Region>[]>(
    () =>
      REGIONS.flatMap((r) =>
        r.shapes.map((shape, index) => ({
          kind: 'polygon' as const,
          id: `${r.key}-${index}`,
          title: t(`${STRINGS_BASE}.region-${r.key}`),
          color: r.color,
          path: shape.map(([latitude, longitude]) => ({ latitude, longitude })),
          data: r,
        })),
      ),
    [t],
  );

  // The selected region's figures, or the whole country's; a newer selection's replace an older's.
  useEffect(() => {
    const controller = new AbortController();
    const load = (request: QueryRequest) => ppp.loadQueryAsync(request, controller.signal);
    Promise.all([
      load(figuresOf(inRegion('account', region), count('accountid'))),
      load(figuresOf(inRegion('contact', region), count('contactid'))),
      load(figuresOf(opportunitiesInRegion(1, region), sum('actualvalue'), count('opportunityid'))),
      load(figuresOf(opportunitiesInRegion(0, region), sum('estimatedvalue'), count('opportunityid'))),
    ]).then(
      (responses) => {
        const figure = (query: number, at: number) => responses[query]?.columnAggregates?.[at]?.formattedValue;
        setFigures({
          of: region,
          figures: {
            accounts: figure(0, 0),
            contacts: figure(1, 0),
            won: figure(2, 0),
            wonDeals: figure(2, 1),
            pipeline: figure(3, 0),
            openDeals: figure(3, 1),
          },
        });
      },
      // Shown as dashes, as a figure the visitor may not see is.
      () => {
        if (!controller.signal.aborted) setFigures({ of: region, figures: {} });
      },
    );
    return () => controller.abort();
  }, [ppp, region]);

  const shown = figures?.of === region ? figures.figures : null;

  return (
    <>
      <div className={styles.toolbar}>
        {/* The label beside its select at its own width: a horizontal Field gives it a third of the
            field, which wraps it. */}
        <div className={styles.labeled}>
          <Label htmlFor={styleId}>{t(`${STRINGS_BASE}.map-style`)}</Label>
          <Select id={styleId} className={styles.select} value={style} onChange={(_, data) => setStyle(data.value as MapStyle)}>
            {STYLES.map((value) => (
              <option key={value} value={value}>
                {t(`${STRINGS_BASE}.style-${value}`)}
              </option>
            ))}
          </Select>
        </div>
        <Button disabled={!region} onClick={() => setRegion(null)}>
          {t(`${STRINGS_BASE}.all-regions`)}
        </Button>
      </div>
      <Map<Region>
        items={regionItems}
        multiTable
        viewIds={MAP_VIEW_IDS}
        defaultViewId={ACCOUNTS_AND_CONTACTS_VIEW_ID}
        allowFiltering
        allowSavedViews
        savedViewsKey="map-demo"
        mapStyle={style}
        showAddressSearch
        addressSearchCountries={UNITED_STATES}
        center={LOWER_FORTY_EIGHT}
        zoom={4}
        height="520px"
        // Printed under the region shown, or every region's heading.
        showPrintButton
        printTitle={regionTitle}
        // A region clicked selects it; a row's marker just opens its popup.
        onItemClick={({ item }) => {
          const clicked = item.data;
          if (clicked && 'states' in clicked) setRegion(clicked);
        }}
        // A region's popup is its figures, with a spinner until they come; a row's says what it is and where.
        renderPopup={(item) => {
          const clicked = item.data;
          if (clicked && 'states' in clicked) return <RegionCard region={clicked} figures={clicked === region ? shown : null} />;
          const row = clicked && 'record' in clicked ? clicked : null;
          // Its name linked to its record's page, where the portal registers one for its table.
          const page = row?.record.id ? findRecordPage(recordPages, row.record.tableName) : undefined;
          return (
            <>
              <Text weight="semibold" block>
                {page && row?.record.id ? <RecordLink href={recordPageUrl(page, row.record.id)}>{item.title}</RecordLink> : item.title}
              </Text>
              {row && (
                <Text size={200} block className={styles.muted}>
                  {t(`${STRINGS_BASE}.kind-${row.record.tableName}`)} · {place(row.record)}
                </Text>
              )}
            </>
          );
        }}
      />
      <h3 className={styles.figuresHeading}>
        {regionTitle}
        {/* The cards show a dash for a figure that hasn't come yet: the spinner says it is coming. */}
        {!shown && <Spinner size="tiny" />}
      </h3>
      <p style={{ margin: '0 0 12px' }}>{region ? region.states.join(', ') : t(`${STRINGS_BASE}.hint`)}</p>
      {/* The figures the popup shows, given to the cards: worked out once for both. */}
      <SummaryCards>
        <SummaryCard icon={<Building24Regular />} label={t(`${STRINGS_BASE}.accounts`)} value={shown?.accounts ?? null} />
        <SummaryCard icon={<PeopleTeam24Regular />} label={t(`${STRINGS_BASE}.contacts`)} value={shown?.contacts ?? null} />
        <SummaryCard
          icon={<Trophy24Regular />}
          label={t(`${STRINGS_BASE}.won`)}
          value={shown?.won ?? null}
          {...(shown?.wonDeals ? { caption: t(`${STRINGS_BASE}.won-deals`, [shown.wonDeals]) } : {})}
        />
        <SummaryCard
          icon={<ArrowTrendingLines24Regular />}
          label={t(`${STRINGS_BASE}.pipeline`)}
          value={shown?.pipeline ?? null}
          {...(shown?.openDeals ? { caption: t(`${STRINGS_BASE}.open-deals`, [shown.openDeals]) } : {})}
        />
      </SummaryCards>
    </>
  );
}
